/**
 * Cupom de PREPARO — fundo branco e texto preto (mesmo padrão do cupom de entrega, TM-T20X).
 * Usado para Delivery e Mesa. Mesmas regras: ** PREPARO **, #codigo, hora, CLIENTE,
 * MESA: NN e COMANDA: #X (quando for Mesa), — ITENS —, itens (→ COMBO, subitens, >>> obs), data/hora.
 */
import axios from "axios";
import { smartPrint } from "./printConnector";
import { getEmpresaData, getImpressaoConfig, buildLogoUrl, buildEmpresaPayload, buildFontScaleStyle, mesaLabelPrint, getAllProductsCached, shouldPrintPreparoForProduct, pizzaDisplayName, formatCupomAgendamento, medidaDoItem, quantidadeNoCupom } from "./cupomFormatters";

const API = "/api";

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

function formatTimeOnly(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatDateTimeFull(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Imprime o cupom de PREPARO em nova janela (fundo branco, texto preto — mesmo estilo do cupom de entrega).
 * Mesmas regras para Delivery e Mesa: itens com → COMBO, subitens, etapas, >>> obs.
 * Se o pedido não tiver itens e tiver id, tenta buscar o pedido completo.
 * Para Mesa: passar mesa_numero e comanda (opcional) para exibir "MESA: 01" e "COMANDA: #X".
 * @param {Object} pedido - { items, codigo, created_at, cliente_nome } e opcionalmente { mesa_numero, comanda }
 */

export async function imprimirCupomPreparo(pedido) {
  let p = { ...pedido };

  if ((!p.items || !p.items.length) && p.id) {
    try {
      const res = await axios.get(`${API}/pedidos/${p.id}`, getAuthHeader());
      if (res.data && res.data.items && res.data.items.length) p = res.data;
    } catch (_) {}
  }

  const codigoStr = String((p.codigo ?? "").replace(/^#+/, "")).padStart(5, "0");

  let itensHtml = "";
  // Filtrar itens cancelados (deleted) E itens cujo produto desabilitou preparo no modulo do pedido.
  // print_config.preparo_modules: ["Balcao","Delivery","Mesas","Cardapio"] (default todos).
  // Se o modulo do pedido NAO esta na lista do produto, o item nao vai pro cupom de preparo.
  const _moduloPedido = p.modulo || null;
  let _allProducts = [];
  try { _allProducts = await getAllProductsCached(); } catch (_) { _allProducts = []; }
  const items = (p.items || []).filter(it => {
    if (it.deleted) return false;
    if (!_moduloPedido) return true;  // sem modulo conhecido, mantem comportamento atual (imprime)
    const pid = it.id || it.product_id;
    const prod = pid ? _allProducts.find(x => String(x.id) === String(pid)) : null;
    if (!prod) return true;  // produto nao encontrado no cache, fail-safe: imprime
    return shouldPrintPreparoForProduct(prod, _moduloPedido);
  });
  // Se nenhum item sobrou (todos os items do pedido tem preparo desabilitado no modulo atual),
  // nao imprime cupom de preparo nenhum — retorna ok sem fazer nada.
  if (items.length === 0) {
    return { success: true, skipped: true, reason: 'todos os items tem preparo desabilitado neste modulo' };
  }
  const sep = '<div class="center" style="font-size:14px;font-weight:bold;color:#333;margin:6px 0;">------------------</div>';
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const qty = it.quantidade || it.qty || 1;
    const qtdTxt = quantidadeNoCupom(qty, medidaDoItem(it, _allProducts));
    const nome = pizzaDisplayName(it);
    const isCombo = (it.combo_type || "").toLowerCase() === "combo";
    const isPizza = it.product_type === "pizza" || !!it.pizza;

    itensHtml += "<div style='margin-bottom:8px;'>";
    if (isPizza) {
      itensHtml += `<div style='font-size:22px;font-weight:900;background:#000;color:#fff;padding:3px 6px;display:inline-block;'>🍕 ${qtdTxt} ${nome.toUpperCase()}</div>`;
    } else {
      itensHtml += `<div style='font-size:20px;font-weight:bold;'>${qtdTxt} ${nome}${isCombo ? " ///COMBO" : ""}</div>`;
    }

    if (it.subitems && it.subitems.length) {
      // Header de etapa: aparece centralizado e pequeno apenas quando step_name
      // MUDA entre subitems (1o grupo nao tem header — implicito).
      let lastStepName = null;
      let isFirstSub = true;
      for (const s of it.subitems) {
        const _sq = Number(s.quantidade ?? s.qty ?? 1) || 1;
        const nm = (_sq > 1 ? `${_sq}x ` : "") + (s.nome || s.name || "");
        const stepName = s.step_name || "";
        if (!nm) continue;
        if (!isFirstSub && stepName && stepName !== lastStepName) {
          itensHtml += `<div style='text-align:center;font-size:13px;font-weight:bold;color:#000;margin:4px 0 2px;'>${stepName.toUpperCase()}</div>`;
        }
        lastStepName = stepName;
        isFirstSub = false;
        if (isPizza) {
          itensHtml += `<div style='margin-left:18px;font-size:19px;font-weight:bold;color:#000;'>• ${nm}</div>`;
        } else {
          itensHtml += `<div style='margin-left:16px;font-size:18px;font-weight:bold;color:#333;'>${nm}</div>`;
        }
      }
    }
    if ((!it.subitems || !it.subitems.length) && it.etapas && it.etapas.length) {
      for (const ep of it.etapas) {
        const itensEtapa = ep.itens || ep.items || [];
        for (const ei of itensEtapa) {
          const lab = typeof ei === "string" ? ei : (ei.nome || ei.name || "");
          if (lab) itensHtml += `<div style='margin-left:16px;font-size:18px;font-weight:bold;color:#333;'>${lab}</div>`;
        }
      }
    }

    if ((it.observacao || "").trim())
      itensHtml += `<div style='background:#000;color:#fff;text-align:center;font-size:22px;font-weight:900;padding:4px 6px;margin:6px 0;'>${it.observacao}</div>`;

    itensHtml += "</div>";
    if (i < items.length - 1) itensHtml += sep;
  }
  if (!itensHtml) itensHtml = "<div style='font-size:18px;font-weight:bold;color:#333;'>Nenhum item</div>";

  const tituloItens = p._tituloItens || "— ITENS —";
  // Carrega config de impressao antes de montar o HTML (pra incluir font_scale)
  const [impressaoConfig, companyData] = await Promise.all([getImpressaoConfig(), getEmpresaData()]);
  const _fontScaleCss = buildFontScaleStyle(impressaoConfig.font_scale);

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Preparo ${codigoStr}</title>
  <style>
    @page { size: 80mm auto; margin: 3mm; }
    body { margin: 0; padding: 8px; font-family: "Courier New", monospace; font-size: 13px; font-weight: bold; background: none; color: #000000; }
    .line { border-bottom: 1px dashed #000; margin: 6px 0; }
    .center { text-align: center; }
    .section { color: #000; font-weight: bold; margin: 4px 0; }
    @media print { body { max-width: 72.1mm; margin: 0 auto; background: none; color: #000; } }
    ${_fontScaleCss}
  </style>
</head>
<body style="max-width:72.1mm;margin:0 auto;background:none;color:#000;">
  <div><br><br><br><br><br><br><br></div>
  ${p.scheduled_at ? `
    <div class="center" style="border:3px solid #000;padding:6px 4px;margin:0 0 8px 0;background:#000;color:#fff;">
      <div style="font-size:18px;font-weight:900;letter-spacing:1px;">** PEDIDO AGENDADO **</div>
      <div style="font-size:11px;font-weight:bold;margin-top:2px;">ENTREGA PREVISTA</div>
      <div style="font-size:22px;font-weight:900;letter-spacing:0.5px;">${formatCupomAgendamento(p.scheduled_at)}</div>
    </div>
  ` : ""}
  <div class="center" style="font-size:28px;font-weight:900;">** PREPARO **</div>
  ${(p.modulo || "").toLowerCase() === "ifood" ? `<div class="center" style="font-size:20px;font-weight:bold;margin:4px 0;padding:2px;border:2px solid #000;">iFOOD</div>` : ""}
  <div class="center" style="font-size:20px;font-weight:bold;margin:4px 0;">#${codigoStr}</div>
  ${(["balcao", "totem"].includes((p.modulo || "").toLowerCase()) && p.senha_balcao) ? `<div class="center" style="font-size:28px;font-weight:900;margin:4px 0;">SENHA: ${p.senha_balcao}</div>` : ""}
  <div class="center" style="font-size:13px;font-weight:bold;">${formatTimeOnly(p.created_at)}</div>
  <div class="line"></div>
  <div class="center" style="font-weight:bold;">CLIENTE: ${p.cliente_nome || "—"}</div>
  ${p.mesa_numero != null ? `<div class="center" style="font-size:14px;font-weight:bold;">${(p.mesa_nome && String(p.mesa_nome).trim()) ? String(p.mesa_nome).trim() : `MESA: ${String(p.mesa_numero).padStart(2, "0")}`}</div>` : ""}
  ${(p.comanda != null && String(p.comanda || "").trim() !== "") ? `<div class="center" style="font-size:14px;font-weight:bold;">COMANDA: #${String(p.comanda).trim().replace(/^#+/, "")}</div>` : ""}
  <div class="line"></div>
  <div class="center section">${tituloItens}</div>
  <div class="line"></div>
  ${itensHtml}
  <div class="line"></div>
  <div class="center" style="font-size:11px;font-weight:bold;color:#333;">${formatDateTimeFull(p.created_at)}</div>
</body>
</html>`;

  // Regras de impressão (impressaoConfig + companyData ja carregados acima)
  const printerNames = impressaoConfig.preparo || [];

  const logoUrl = buildLogoUrl(companyData);
  const empresaData = buildEmpresaPayload(companyData, logoUrl);

  // Verificar se itens têm rotas diferentes (ticket_route) — se sim, agrupar e enviar cupom por rota
  const rotasSet = new Set();
  for (const it of items) {
    const rota = (it.ticket_route || "COZINHA").toUpperCase();
    rotasSet.add(rota);
  }

  if (rotasSet.size <= 1) {
    // Rota única — enviar normalmente
    const rota = rotasSet.values().next().value || "COZINHA";
    return smartPrint(html, rota, printerNames, p, empresaData, p.modulo || null);
  }

  // Múltiplas rotas — gerar cupom separado por rota
  const results = [];
  for (const rota of rotasSet) {
    const rotaItems = items.filter(it => (it.ticket_route || "COZINHA").toUpperCase() === rota);
    if (rotaItems.length === 0) continue;

    let rotaItensHtml = "";
    for (let i = 0; i < rotaItems.length; i++) {
      const it = rotaItems[i];
      const qty = it.quantidade || it.qty || 1;
      const qtdTxt = quantidadeNoCupom(qty, medidaDoItem(it, _allProducts));
      const nome = pizzaDisplayName(it);
      const isCombo = (it.combo_type || "").toLowerCase() === "combo";
      const isPizza = it.product_type === "pizza" || !!it.pizza;
      rotaItensHtml += "<div style='margin-bottom:8px;'>";
      if (isPizza) {
        rotaItensHtml += `<div style='font-size:22px;font-weight:900;background:#000;color:#fff;padding:3px 6px;display:inline-block;'>🍕 ${qtdTxt} ${nome.toUpperCase()}</div>`;
      } else {
        rotaItensHtml += `<div style='font-size:20px;font-weight:bold;'>${qtdTxt} ${nome}${isCombo ? " ///COMBO" : ""}</div>`;
      }
      if (it.subitems && it.subitems.length) {
        // Mesma logica do caminho principal: header pequeno centralizado em mudanca de etapa.
        let lastStepName = null;
        let isFirstSub = true;
        for (const s of it.subitems) {
          const _sq = Number(s.quantidade ?? s.qty ?? 1) || 1;
        const nm = (_sq > 1 ? `${_sq}x ` : "") + (s.nome || s.name || "");
          const stepName = s.step_name || "";
          if (!nm) continue;
          if (!isFirstSub && stepName && stepName !== lastStepName) {
            rotaItensHtml += `<div style='text-align:center;font-size:13px;font-weight:bold;color:#000;margin:4px 0 2px;'>${stepName.toUpperCase()}</div>`;
          }
          lastStepName = stepName;
          isFirstSub = false;
          if (isPizza) {
            rotaItensHtml += `<div style='margin-left:18px;font-size:19px;font-weight:bold;color:#000;'>• ${nm}</div>`;
          } else {
            rotaItensHtml += `<div style='margin-left:16px;font-size:18px;font-weight:bold;color:#333;'>${nm}</div>`;
          }
        }
      }
      if ((!it.subitems || !it.subitems.length) && it.etapas && it.etapas.length) {
        for (const ep of it.etapas) {
          for (const ei of (ep.itens || ep.items || [])) {
            const lab = typeof ei === "string" ? ei : (ei.nome || ei.name || "");
            if (lab) rotaItensHtml += `<div style='margin-left:16px;font-size:18px;font-weight:bold;color:#333;'>${lab}</div>`;
          }
        }
      }
      if ((it.observacao || "").trim())
        rotaItensHtml += `<div style='background:#000;color:#fff;text-align:center;font-size:22px;font-weight:900;padding:4px 6px;margin:6px 0;'>${it.observacao}</div>`;
      rotaItensHtml += "</div>";
      if (i < rotaItems.length - 1) rotaItensHtml += sep;
    }

    const rotaHtml = html.replace(itensHtml, rotaItensHtml);
    try {
      const r = await smartPrint(rotaHtml, rota, printerNames, p, empresaData, p.modulo || null);
      results.push(r);
    } catch (e) {
      results.push({ success: false, error: e?.message });
    }
  }
  return { success: results.every(r => r?.success) };
}
